import { PcmResampler } from './pcm-resampler';

/** Converts microphone samples to signed little-endian PCM without depending on a codec. */
export function microphonePcm(samples: Float32Array, inputRate: number, outputRate = 16_000) {
  const ratio = inputRate / outputRate;
  const count = Math.floor(samples.length / ratio);
  const bytes = new Uint8Array(count * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < count; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.max(start + 1, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let index = start; index < Math.min(end, samples.length); index++)
      sum += samples[index] ?? 0;
    const value = Math.max(-1, Math.min(1, sum / (end - start)));
    view.setInt16(i * 2, value < 0 ? value * 32768 : value * 32767, true);
  }
  return bytes;
}

export function pcmBase64(bytes: Uint8Array) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** HTTP transport chunks can split a sample in half. Retain that byte for the next chunk. */
export class PcmDecoder {
  private pending: number | undefined;

  decode(chunk: Uint8Array) {
    const bytes = this.pending === undefined ? chunk : new Uint8Array([this.pending, ...chunk]);
    this.pending = bytes.length % 2 ? bytes[bytes.length - 1] : undefined;
    const samples = new Float32Array(Math.floor(bytes.length / 2));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let i = 0; i < samples.length; i++) samples[i] = view.getInt16(i * 2, true) / 32768;
    return samples;
  }
}

export interface PlaybackSnapshot {
  playedMs: number;
  generatedMs: number;
}

/** The browser playback clock is authoritative for interruption, not HTTP receipt time. */
export class PcmPlayer {
  private context: AudioContext;
  private output: GainNode;
  private nodes = new Set<AudioBufferSourceNode>();
  private nextFrame = 0;
  private fadeAt = 0;
  private segments: { start: number; duration: number }[] = [];
  private playedSeconds = 0;
  private generatedSeconds = 0;
  private generation = 0;

  constructor(
    private onState: (speaking: boolean) => void,
    onInterrupted?: () => void,
  ) {
    if (typeof AudioContext === 'undefined')
      throw new Error('This browser does not support live audio. You can continue in chat.');
    // The hardware chooses its rate; continuous conversion happens before playback.
    this.context = new AudioContext();
    this.output = this.context.createGain();
    this.output.gain.value = 0;
    this.output.connect(this.context.destination);
    this.context.onstatechange = () => {
      if (this.context.state !== 'running' && this.context.state !== 'closed') onInterrupted?.();
    };
  }

  async resume() {
    if (this.context.state !== 'running') await this.context.resume();
  }

  get interrupted() {
    return this.context.state !== 'running';
  }

  snapshot(): PlaybackSnapshot {
    const elapsed = this.segments.reduce(
      (sum, segment) =>
        sum + Math.min(segment.duration, Math.max(0, this.context.currentTime - segment.start)),
      this.playedSeconds,
    );
    return {
      playedMs: Math.round(Math.min(this.generatedSeconds, elapsed) * 1000),
      generatedMs: Math.round(this.generatedSeconds * 1000),
    };
  }

  private async waitForCapacity(duration: number, signal: AbortSignal, version: number) {
    while (
      !signal.aborted &&
      this.generation === version &&
      this.nextFrame / this.context.sampleRate - this.context.currentTime + duration > 2
    ) {
      await new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          signal.removeEventListener('abort', done);
          resolve();
        };
        const timer = setTimeout(done, 25);
        signal.addEventListener('abort', done, { once: true });
      });
    }
    return !signal.aborted && this.generation === version;
  }

  private holdGain(at: number) {
    const gain = this.output.gain;
    if (typeof gain.cancelAndHoldAtTime === 'function') gain.cancelAndHoldAtTime(at);
    else {
      const value = gain.value;
      gain.cancelScheduledValues(at);
      gain.setValueAtTime(value, at);
    }
  }

  private async schedule(
    samples: Float32Array<ArrayBuffer>,
    signal: AbortSignal,
    version: number,
    beginsUtterance: boolean,
  ) {
    const rate = this.context.sampleRate;
    if (!(await this.waitForCapacity(samples.length / rate, signal, version))) return;
    if (signal.aborted || this.generation !== version) return;
    const buffer = this.context.createBuffer(1, samples.length, rate);
    buffer.copyToChannel(samples, 0);
    const node = this.context.createBufferSource();
    node.buffer = buffer;
    node.connect(this.output);
    const now = this.context.currentTime;
    const frame = Math.ceil(now * rate);
    const restarting = this.nextFrame <= frame;
    // One short jitter buffer at startup/true underrun. Never insert padding
    // merely because the next buffer arrives close to the previous buffer's end.
    const atFrame = restarting ? frame + Math.ceil(rate * 0.08) : this.nextFrame;
    const at = atFrame / rate;
    const end = (atFrame + samples.length) / rate;
    const fade = Math.min(0.004, (end - at) / 2);
    const gain = this.output.gain;
    if (restarting || beginsUtterance) {
      // Independently synthesized responses can meet at different amplitudes.
      // Keep their tiny edge fades even when scheduled without a time gap.
      gain.setValueAtTime(0, at);
      gain.linearRampToValueAtTime(1, at + fade);
    } else if (this.fadeAt > now) {
      // A following buffer arrived in time: cancel the old tail fade so the
      // waveform stays at unity across transport boundaries.
      gain.cancelScheduledValues(this.fadeAt);
      // In a very short first response, the startup ramp can end at this same
      // timestamp. Restore its unity endpoint as well as the flat continuation.
      gain.linearRampToValueAtTime(1, this.fadeAt);
    } else {
      // Recover smoothly if a packet arrived during the very short tail fade.
      this.holdGain(now);
      gain.linearRampToValueAtTime(1, Math.min(now + fade, end - fade));
    }
    this.fadeAt = end - fade;
    gain.setValueAtTime(1, this.fadeAt);
    gain.linearRampToValueAtTime(0, end);
    const segment = { start: at, duration: samples.length / rate };
    this.segments.push(segment);
    this.generatedSeconds += segment.duration;
    this.nextFrame = atFrame + samples.length;
    this.nodes.add(node);
    node.onended = () => {
      this.nodes.delete(node);
      this.segments = this.segments.filter((item) => item !== segment);
      this.playedSeconds += segment.duration;
      node.disconnect();
      if (!this.nodes.size) this.onState(false);
    };
    node.start(at);
    this.onState(true);
  }

  async stream(response: Response, signal: AbortSignal) {
    if (!response.body) throw new Error('Mio returned no speech audio.');
    if (!/audio\/(?:pcm|l16)/i.test(response.headers.get('Content-Type') ?? '')) {
      throw new Error('Mio speech returned an unsupported audio format.');
    }
    const sampleRate = Number(response.headers.get('X-Audio-Sample-Rate') ?? 24_000);
    if (![16_000, 22_050, 24_000, 44_100, 48_000].includes(sampleRate)) {
      throw new Error('Mio speech returned an unsupported sample rate.');
    }
    const version = this.generation;
    const decoder = new PcmDecoder();
    const resampler = new PcmResampler(sampleRate, this.context.sampleRate);
    const blockFrames = Math.ceil(this.context.sampleRate * 0.08);
    let block = new Float32Array(blockFrames);
    let filled = 0;
    let firstBlock = true;
    const enqueue = async (samples: Float32Array) => {
      for (let offset = 0; offset < samples.length;) {
        if (signal.aborted || this.generation !== version) return;
        const count = Math.min(blockFrames - filled, samples.length - offset);
        block.set(samples.subarray(offset, offset + count), filled);
        filled += count;
        offset += count;
        if (filled === blockFrames) {
          await this.schedule(block, signal, version, firstBlock);
          firstBlock = false;
          block = new Float32Array(blockFrames);
          filled = 0;
        }
      }
    };
    const reader = response.body.getReader();
    const cancel = () => {
      void reader.cancel().catch(() => undefined);
    };
    signal.addEventListener('abort', cancel, { once: true });
    let ended = false;
    try {
      while (!signal.aborted && this.generation === version) {
        const chunk = await reader.read();
        if (signal.aborted || this.generation !== version) break;
        if (chunk.done) {
          ended = true;
          break;
        }
        // Preserve decoder and resampler history across arbitrary HTTP chunks.
        // Bound CPU work and buffered audio even for a large fetch chunk.
        for (let offset = 0; offset < chunk.value.length; offset += 16_384) {
          if (signal.aborted || this.generation !== version) break;
          const samples = decoder.decode(chunk.value.subarray(offset, offset + 16_384));
          await enqueue(resampler.push(samples));
        }
      }
      if (ended && !signal.aborted && this.generation === version) {
        await enqueue(resampler.finish());
        if (filled) await this.schedule(block.slice(0, filled), signal, version, firstBlock);
      }
    } finally {
      signal.removeEventListener('abort', cancel);
      if (!ended) await reader.cancel().catch(() => undefined);
      reader.releaseLock();
    }
  }

  stop() {
    const snapshot = this.snapshot();
    this.generation++;
    const now = this.context.currentTime;
    this.holdGain(now);
    this.output.gain.linearRampToValueAtTime(0, now + 0.004);
    for (const node of this.nodes) {
      node.onended = () => node.disconnect();
      node.stop(now + 0.004);
    }
    this.nodes.clear();
    this.nextFrame = 0;
    this.fadeAt = 0;
    this.segments = [];
    this.playedSeconds = 0;
    this.generatedSeconds = 0;
    this.onState(false);
    return snapshot;
  }

  close() {
    const fading = this.nodes.size > 0 && this.context.state === 'running';
    this.stop();
    // Let the cancellation ramp render before releasing the device.
    if (fading) setTimeout(() => void this.context.close(), 8);
    else void this.context.close();
  }
}

/** Local speaker check: no microphone permission, provider session, or billable speech request. */
export async function playTestTone() {
  if (typeof AudioContext === 'undefined')
    throw new Error('This browser does not support audio playback.');
  const context = new AudioContext();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error('The speaker test timed out. Check your audio output and try again.')),
      4000,
    );
  });
  try {
    await Promise.race([context.resume(), deadline]);
    if (context.state !== 'running')
      throw new Error('Your browser paused audio playback. Try the speaker test again.');
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 440;
    gain.gain.setValueAtTime(0, context.currentTime);
    gain.gain.linearRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.setValueAtTime(0.12, context.currentTime + 0.35);
    gain.gain.linearRampToValueAtTime(0, context.currentTime + 0.4);
    oscillator.connect(gain).connect(context.destination);
    const ended = new Promise<void>((resolve) => {
      oscillator.onended = () => resolve();
    });
    oscillator.start();
    oscillator.stop(context.currentTime + 0.42);
    await Promise.race([ended, deadline]);
  } finally {
    clearTimeout(timer);
    await context.close();
  }
}
